import { Action } from 'redux';
import { ContractAddendum } from '../../../models/Entities/Contracts/ContractAddendum';
import { ContractNegotiation } from '../../../models/Entities/Contracts/ContractNegotiation';
import { ContractNegotiationVersion } from '../../../models/Entities/Contracts/ContractNegotiationVersion';
import { Contract } from '../../../models/Entities/Contracts/Contract';
import { ContractState, ContractUnloadState } from '../../states/ContractState';

const FindContract = (currentState: ContractState, contractId: number): Contract | undefined =>
  currentState.list?.find((item: Contract) => item.id === contractId);

const UpdateContractInList = (currentState: ContractState, updatedContract?: Contract): Contract[] => {
  if (!updatedContract) return currentState.list || [];
  return (currentState.list || []).map((item: Contract) =>
    item.id === updatedContract.id ? updatedContract : item
  );
};

const BuildState = (currentState: ContractState, updatedContract?: Contract): ContractState => ({
  ...currentState,
  list: UpdateContractInList(currentState, updatedContract),
});

const UpsertVersion = (
  versions: ContractNegotiationVersion[] | undefined,
  version: ContractNegotiationVersion
): ContractNegotiationVersion[] => {
  const current = versions || [];
  const exists = current.some((v: ContractNegotiationVersion) => v.id === version.id);
  if (!exists) return [...current, version];
  return current.map((v: ContractNegotiationVersion) => (v.id === version.id ? version : v));
};

const UpdateNegotiationInContract = (
  contract: Contract,
  negotiationId: string,
  updater: (neg: ContractNegotiation) => ContractNegotiation
): Contract => ({
  ...contract,
  negotiations: (contract.negotiations || []).map((n: ContractNegotiation) =>
    n.id === negotiationId ? updater(n) : n
  ),
  addendums: (contract.addendums || []).map((addendum: ContractAddendum) => ({
    ...addendum,
    negotiations: (addendum.negotiations || []).map((n: ContractNegotiation) =>
      n.id === negotiationId ? updater(n) : n
    ),
  } as ContractAddendum)),
});

const HandleNegotiationUpdate = (
  currentState: ContractState,
  contractId: number,
  negotiationId: string,
  updater: (neg: ContractNegotiation) => ContractNegotiation
): ContractState => {
  const contract = FindContract(currentState, contractId);
  if (!contract) return currentState;
  return BuildState(currentState, UpdateNegotiationInContract(contract, negotiationId, updater));
};

const HandleVersionUpsert = (
  currentState: ContractState,
  contractId: number,
  version: ContractNegotiationVersion
): ContractState =>
  HandleNegotiationUpdate(currentState, contractId, version.contractNegotiationId, (neg: ContractNegotiation) => ({
    ...neg,
    versions: UpsertVersion(neg.versions, version),
  }));

const HandleVersionStatus = (
  currentState: ContractState,
  contractId: number,
  version: ContractNegotiationVersion,
  partial: Partial<ContractNegotiationVersion>
): ContractState =>
  HandleNegotiationUpdate(currentState, contractId, version.contractNegotiationId, (neg: ContractNegotiation) => ({
    ...neg,
    versions: (neg.versions || []).map((v: ContractNegotiationVersion) =>
      v.id === version.id ? ({ ...v, ...partial } as ContractNegotiationVersion) : v
    ),
  }));

const HandleVersionDelete = (
  currentState: ContractState,
  contractId: number,
  contractNegotiationId: string,
  versionId: string
): ContractState =>
  HandleNegotiationUpdate(currentState, contractId, contractNegotiationId, (neg: ContractNegotiation) => ({
    ...neg,
    versions: (neg.versions || []).filter((v: ContractNegotiationVersion) => v.id !== versionId),
  }));

const Handlers: Record<string, (currentState: ContractState, action: any) => ContractState> = {
  CONTRACTNEGOTIATIONVERSION_GET_ALL_REQUEST_ACTION: (currentState, action) =>
    HandleNegotiationUpdate(currentState, action.contractId, action.contractNegotiationId, (neg) => ({
      ...neg,
      isGettingNegotiationVersions: true,
      isGettingNegotiationVersionsSuccess: false,
      FailGettingNegotiationVersions: false,
      ErrorGettingNegotiationVersions: undefined,
    } as ContractNegotiation)),
  CONTRACTNEGOTIATIONVERSION_GET_ALL_RECEIVE_ACTION: (currentState, action) =>
    HandleNegotiationUpdate(currentState, action.contractId, action.contractNegotiationId, (neg) => ({
      ...neg,
      versions: action.contractNegotiationVersions,
      isGettingNegotiationVersions: false,
      isGettingNegotiationVersionsSuccess: true,
      FailGettingNegotiationVersions: false,
      ErrorGettingNegotiationVersions: undefined,
    } as ContractNegotiation)),
  CONTRACTNEGOTIATIONVERSION_GET_ALL_ERROR_ACTION: (currentState, action) =>
    HandleNegotiationUpdate(currentState, action.contractId, action.contractNegotiationId, (neg) => ({
      ...neg,
      isGettingNegotiationVersions: false,
      isGettingNegotiationVersionsSuccess: false,
      FailGettingNegotiationVersions: true,
      ErrorGettingNegotiationVersions: action.error,
    } as ContractNegotiation)),
  CONTRACTNEGOTIATIONVERSION_GET_BY_ID_RECEIVE_ACTION: (currentState, action) =>
    HandleVersionUpsert(currentState, action.contractId, action.contractNegotiationVersion),
  CONTRACTNEGOTIATIONVERSION_CREATE_RECEIVE_ACTION: (currentState, action) =>
    HandleVersionUpsert(currentState, action.contractId, action.contractNegotiationVersion),
  CONTRACTNEGOTIATIONVERSION_UPDATE_REQUEST_ACTION: (currentState, action) =>
    HandleVersionStatus(currentState, action.contractId, action.contractNegotiationVersion, {
      isUpdating: true,
      isUpdatedSuccessfully: undefined,
      failOnUpdating: false,
      error: undefined,
    }),
  CONTRACTNEGOTIATIONVERSION_UPDATE_RECEIVE_ACTION: (currentState, action) =>
    HandleVersionUpsert(currentState, action.contractId, action.contractNegotiationVersion),
  CONTRACTNEGOTIATIONVERSION_UPDATE_ERROR_ACTION: (currentState, action) =>
    HandleVersionStatus(currentState, action.contractId, action.contractNegotiationVersion, {
      isUpdating: false,
      isUpdatedSuccessfully: false,
      failOnUpdating: true,
      error: action.error,
    }),
  CONTRACTNEGOTIATIONVERSION_DELETE_RECEIVE_ACTION: (currentState, action) =>
    HandleVersionDelete(currentState, action.contractId, action.contractNegotiationId, action.id),
};

export default function ContractNegotiationVersionsReducer(
  state: ContractState | undefined,
  action: Action
): ContractState {
  const currentState = state ?? ContractUnloadState;
  const a: any = action;
  const handler = Handlers[a.type];
  if (!handler) return currentState;
  return handler(currentState, a);
}
